import { Classes, Divider, Tooltip } from '@blueprintjs/core';
import { classNames } from '@axo/shared/util/dom';
import {
  loan_application_hooks,
  sanctioned_individual_hooks,
  useApplicationDetail,
  usePerson,
} from '@axo/shared/data-access/hooks';
import styles from './ApplicationCard.module.css';
import { StatusTag } from '../StatusTag';
import { ReactNode } from 'react';
import { AceCard, IAceCard } from '../AceCard';
import { CopyTextButton } from '../CopyTextButton';
import { loan_application } from '@axo/shared/data-access/types';
import { ProductIcon } from './ProductIcon';
import { LockTag } from './components/LockTag.tsx';
import { OwnerTag } from './components/OwnerTag.tsx';
import { Dates } from './components/Dates.tsx';
import { SmallTag } from '../SmallTag';
import { Link } from 'react-router-dom';
import React from 'react';
import { useShouldUseBuckets } from '../../hooks/utils/useShouldUseBuckets.ts';
import { ActionsContainer } from './components/ActionsContainer.tsx';

export interface IApplicationCard
  extends Omit<
    IAceCard,
    'id' | 'title' | 'leftIcon' | 'rightTag' | 'children'
  > {
  marketCountry: string;
  applicationID: string;
  product?: loan_application.Product;
  link?: ReactNode;
}

export function ApplicationCard({
  marketCountry,
  applicationID,
  link,
  product,
  ...props
}: IApplicationCard) {
  const app = useApplicationDetail(applicationID);
  let metaKeys = null;
  if (app.data?.Meta) {
    metaKeys = Object.entries(app.data?.Meta).map(([key, value]) => {
      return {
        [key]: value,
      };
    });
  }

  return (
    <AceCard
      {...props}
      classes={{
        root: classNames(
          styles.card,
          product === loan_application.Product.CreditCard && styles.creditCard,
          product === loan_application.Product.UnsecuredLoan &&
            styles.unsecuredLoan,
          product === loan_application.Product.SecuredLoan &&
            styles.securedLoan,
        ),
      }}
      id={`ApplicationCard.${applicationID}`}
      title={app.data?.Product ?? 'Application'}
      metaTags={metaKeys}
      leftIcon={app.data && <ProductIcon product={app.data.Product} />}
    >
      <ApplicationCardContent
        applicationID={applicationID}
        marketCountry={marketCountry}
      />
      {link}
    </AceCard>
  );
}

function ApplicationCardContent({
  applicationID,
  marketCountry,
}: {
  applicationID: string;
  marketCountry: string;
}) {
  const app = useApplicationDetail(applicationID);
  const sourceApp = useApplicationDetail(
    app?.data?.SourceLoanApplicationID ?? undefined,
  );
  const channelDetail = channelValue(app.data?.Channels ?? []);
  const mainApplicantPerson = usePerson(app.data?.PersonID);
  const coApplicantPerson = usePerson(app.data?.CoApplicantPersonID);

  const { data: validationData } =
    loan_application_hooks.useApplicationValidation(applicationID);

  const { data: dependenciesData } =
    loan_application_hooks.useLoanApplicationDependencies(
      app.data?.Status ===
        loan_application.ApplicationStatus
          .ApplicationStatusFetchingExternalDataFailed
        ? applicationID
        : undefined,
    );

  const { data: mainApplicantSactionsData } =
    sanctioned_individual_hooks.useSactions(
      {
        firstName: mainApplicantPerson.data?.FirstName ?? '',
        lastName: mainApplicantPerson.data?.LastName ?? '',
      },
      {
        enabled:
          mainApplicantPerson.data !== undefined &&
          app.data?.Status ===
            loan_application.ApplicationStatus
              .ApplicationStatusBlockedBySanctions,
      },
    );

  const { data: coApplicantSactionsData } =
    sanctioned_individual_hooks.useSactions(
      {
        firstName: coApplicantPerson.data?.FirstName ?? '',
        lastName: coApplicantPerson.data?.LastName ?? '',
      },
      {
        enabled:
          coApplicantPerson.data !== undefined &&
          app.data?.Status ===
            loan_application.ApplicationStatus
              .ApplicationStatusBlockedBySanctions,
      },
    );

  let statusTooltip: string | null | undefined | React.JSX.Element =
    app.data?.Status;

  if (validationData?.Bad) {
    statusTooltip = (
      <div>
        <div>{statusTooltip}</div>
        <p>Validation Errors:</p>
        <ul>
          {validationData.Message && <li>{validationData.Message}</li>}
          {validationData?.Errors?.map(({ Field, Message }) => (
            <li key={Field}>{`${Field}: ${Message}`}</li>
          ))}
        </ul>
      </div>
    );
  }

  if (dependenciesData && dependenciesData.length > 0) {
    const failedDependencies = dependenciesData.filter(
      (dep) => dep.status === loan_application.DependencyStatus.Failed,
    );
    if (failedDependencies.length > 0) {
      statusTooltip = (
        <div>
          <div>{statusTooltip}</div>
          <p>Failed dependencies:</p>
          <ul>
            {failedDependencies.map((dep) => (
              <li key={dep.id}>
                {dep.dependency_name}
                {dep.outcome && (
                  <ul>
                    {Object.entries(dep.outcome).map(([key, value]) => (
                      <li key={key}>
                        {key}: {String(value)}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
      );
    }
  }

  if (
    (mainApplicantSactionsData || coApplicantSactionsData) &&
    app.data?.Status ===
      loan_application.ApplicationStatus.ApplicationStatusBlockedBySanctions
  ) {
    statusTooltip = (
      <div>
        <div>{statusTooltip}</div>
        {mainApplicantSactionsData && (
          <>
            <p>Applicant:</p>
            <ul>
              {mainApplicantSactionsData.map(({ ID, Details, Attributes }) => (
                <li key={ID}>
                  {Details.split('\n').map((text, i) => (
                    <React.Fragment key={[text, i].join('-')}>
                      {text}
                    </React.Fragment>
                  ))}
                  <ul>
                    {Object.entries(Attributes)
                      .filter(([, flagged]) => flagged)
                      .map(([attribute]) => (
                        <li key={attribute}>{attribute}</li>
                      ))}
                  </ul>
                </li>
              ))}
            </ul>
          </>
        )}
        {coApplicantSactionsData && (
          <>
            <p>Co-applicant:</p>
            <ul>
              {coApplicantSactionsData.map(({ ID, Details, Attributes }) => (
                <li key={ID}>
                  {Details.split('\n').map((text, i) => (
                    <React.Fragment key={[text, i].join('-')}>
                      {text}
                    </React.Fragment>
                  ))}
                  <ul>
                    {Object.entries(Attributes)
                      .filter(([, flagged]) => flagged)
                      .map(([attribute]) => (
                        <li key={attribute}>{attribute}</li>
                      ))}
                  </ul>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    );
  }

  const shouldUseBuckets = useShouldUseBuckets();

  return (
    <div>
      <div
        className={classNames(
          styles.wrapper,
          Classes.RUNNING_TEXT,
          Classes.TEXT_SMALL,
        )}
      >
        <span className={styles.label}>Application number:</span>
        <span
          className={classNames(
            Classes.TEXT_MUTED,
            app.isLoading && Classes.SKELETON,
            styles.applicationNumber,
          )}
        >
          {app.data?.HumanReadableID && (
            <CopyTextButton value={app.data.HumanReadableID}>
              {app.data.HumanReadableID}
            </CopyTextButton>
          )}
          {mainApplicantPerson.data?.Fraud && (
            <span>
              <SmallTag icon={'notifications'} intent="danger" minimal round>
                FRAUD
              </SmallTag>
            </span>
          )}
        </span>

        {sourceApp?.data?.HumanReadableID && (
          <>
            <span className={styles.label}>Cloned from:</span>
            <span
              className={classNames(
                Classes.TEXT_MUTED,
                sourceApp.isLoading && Classes.SKELETON,
                styles.applicationNumber,
              )}
            >
              <Link to={`/application/${sourceApp.data.ID}`}>
                {sourceApp.data.HumanReadableID}
              </Link>
            </span>
          </>
        )}

        <span className={styles.label}>Source:</span>
        <span
          className={classNames(
            Classes.TEXT_MUTED,
            app.isLoading && Classes.SKELETON,
            styles.applicationNumber,
          )}
        >
          {channelDetail('InternalIdentityRoleName') ??
            channelDetail('CampaignSource') ??
            '-'}
        </span>

        <span className={styles.label}>Domain:</span>
        <span
          className={classNames(
            Classes.TEXT_MUTED,
            app.isLoading && Classes.SKELETON,
            styles.applicationNumber,
          )}
        >
          {channelDetail('Domain') ?? '-'}
        </span>

        <span className={styles.label}>
          Locked/{shouldUseBuckets ? 'Assigned to:' : 'Owned by:'}
        </span>
        <div className={styles.tagsContainer}>
          {app.data && (
            <>
              <LockTag app={app.data} marketCountry={marketCountry} />
              <div className={styles.divider} />
              <OwnerTag
                marketCountry={marketCountry}
                applicationID={applicationID}
              />
            </>
          )}
        </div>

        <span className={styles.label}>Status:</span>
        <Tooltip content={statusTooltip ?? ''} disabled={!app.data?.Status}>
          <span
            className={classNames(
              Classes.TEXT_MUTED,
              styles.limited,
              styles.statusRow,
              app.isLoading && Classes.SKELETON,
            )}
          >
            <StatusTag status={app.data?.Status ?? ''}>
              {app.data?.Status ?? ''}
            </StatusTag>
          </span>
        </Tooltip>
      </div>

      <ActionsContainer applicationID={applicationID} />

      <Divider />

      <Dates app={app} />
    </div>
  );
}

// channelValue does some trickery to try to guess application source.
// TODO: replace this *if* CJ team ever implements something for it. *COUGH*
function channelValue(channels: loan_application.ChannelSnapshot[]) {
  const descending = channels
    .map((channel) => ({ ...channel, CreatedAt: channel.CreatedAt ?? '' }))
    .sort((a, b) => (b.CreatedAt < a.CreatedAt ? -1 : 1));

  return function (key: keyof loan_application.ChannelSnapshot | 'Domain') {
    return descending.find((channel) => {
      const value = channel[key as keyof loan_application.ChannelSnapshot];
      return value !== undefined && String(value).length > 0;
    })?.[key as keyof loan_application.ChannelSnapshot];
  };
}
